import React, { useState, useCallback } from 'react';
import { object, func, bool, number } from 'prop-types';
import styles from './styles/styles.module.scss';
import directionsIcon from '../../../../assets/images/icons/maps/directions.svg';
import shareIcon from '../../../../assets/images/icons/maps/share.svg';
import UserImageLink from '../../shared/UserImageLink';
import CircleButton from '../../shared/CircleButton';
import FavoriteButton from '../../shared/FavoriteButton';
import MenuPop from '../../shared/MenuPop';
import { monthDayYear } from '../../../utils/time_helpers';
import { ServerCommunicationUtil } from '../../../utils/server_communication_util';
import { StringConstantsMixin } from '../../../application/javascripts/react_components/mixins/shared/string_constants_mixin';
import { getTrackProfilePhotoUrl } from '../../../utils/track_helpers';
import { getImgDefaultStyle } from '../../cards/CardImage/CardImage';
import { LanguageSupportUtil } from '../../../utils/language_support_util';

const MapHeader = ({
  map,
  context,
  isFavorite,
  handleFavoriteClick,
  bannerPhotoId,
  handleDirectionsClick,
  openDownloadModal,
  handleShareClick,
  handleDeleteClick,
  canEdit
}) => {
  let refreshMap;
  let updateHidden;
  const [refreshed, toggleRefreshed] = useState(false);
  const [hidden, toggleHidden] = useState(map.hidden);

  if (context.adminUser) {
    refreshMap = () => {
      ServerCommunicationUtil.getApiEndpoint(
        `/api/alltrails/maps/${map.id}/refresh_map_cache`,
        {},
        () => toggleRefreshed(true),
        err => console.log(err),
        () => {}
      );
    };

    updateHidden = () => {
      ServerCommunicationUtil.postApiEndpoint(
        `/api/alltrails/maps/${map.id}/metadata`,
        { hidden: !hidden },
        () => toggleHidden(!hidden),
        () => {},
        () => {}
      );
    };
  }

  const favoriteClick = () => {
    handleFavoriteClick(map.presentationType, map.id);
  };

  const addAsNewMap = () => {
    window.open(LanguageSupportUtil.wrapUrlSafe(`/trail/new?recording=${map.id}`, context.languageRegionCode), '_blank');
  };

  const getBannerImgStyle = useCallback(() => {
    const mapPhotoUrl = bannerPhotoId
      ? `/api/alltrails/photos/${bannerPhotoId}/image?size=large&key=${ServerCommunicationUtil.apiKey}`
      : getTrackProfilePhotoUrl(map);
    const backgroundImage = `linear-gradient(rgba(0, 0, 0, 0.0), rgba(0, 0, 0, 0.7)), url(${mapPhotoUrl})`;
    return getImgDefaultStyle(map.name, backgroundImage);
  }, [map, bannerPhotoId]);

  const getMenuItems = () => {
    let menuItems = [{ label: StringConstantsMixin.strings.MAP_CREATOR.DOWNLOAD_MAP, callback: openDownloadModal }];

    if (context.adminUser) {
      menuItems = [
        ...menuItems,
        ...[
          {
            label: refreshed ? StringConstantsMixin.strings.MAP_CREATOR.REFRESHED : StringConstantsMixin.strings.MAP_CREATOR.REFRESH,
            callback: refreshMap
          },
          {
            label: hidden ? StringConstantsMixin.strings.TRACK_DETAIL.UNHIDE : StringConstantsMixin.strings.TRACK_DETAIL.HIDE,
            callback: updateHidden
          }
        ]
      ];
    }

    if (canEdit && map.id) {
      menuItems.push({
        label: StringConstantsMixin.strings.MAP_CREATOR.DELETE_MAP,
        callback: handleDeleteClick
      });
    }

    if (context.currentUser && !map.private && !context.mobileBrowser) {
      menuItems.push({
        label: context.adminUser
          ? StringConstantsMixin.strings.MAP_CREATOR.ADD_AS_NEW_TRAIL
          : StringConstantsMixin.strings.MAP_CREATOR.SUGGEST_AS_NEW_TRAIL,
        callback: addAsNewMap
      });
    }

    return menuItems;
  };

  return (
    <div className={styles.container}>
      <div className={styles.banner} style={getBannerImgStyle()}>
        <FavoriteButton className={styles.favoriteButton} isFavorite={isFavorite} handleFavoriteClick={favoriteClick} />
      </div>
      <div className={styles.buttonPanel}>
        <div className={styles.userImage}>
          <UserImageLink context={context} user={map.user} />
        </div>
        <CircleButton
          title={StringConstantsMixin.strings.DIRECTIONS}
          icon={directionsIcon}
          className={styles.circleIcon}
          onClick={handleDirectionsClick}
        />
        <CircleButton title={StringConstantsMixin.strings.SHARE} icon={shareIcon} className={styles.circleIcon} onClick={handleShareClick} />
        <MenuPop variant="circle" items={getMenuItems()} />
      </div>
      <div className={styles.metadata}>
        <div className={styles.dateAndActivity}>
          {monthDayYear(map.created_at)} {map.activity ? `${'\u2022'} ${map.activity.name}` : ''}
        </div>
        <div className={styles.nameAndDescription}>
          <div className={styles.name}>{map.name}</div>
          <div className={styles.description}>{map.description}</div>
        </div>
      </div>
    </div>
  );
};

MapHeader.propTypes = {
  map: object.isRequired,
  context: object.isRequired,
  handleShareClick: func,
  isFavorite: bool,
  handleFavoriteClick: func,
  bannerPhotoId: number
};

MapHeader.defaultProps = {
  bannerPhotoId: null,
  handleShareClick: () => {},
  isFavorite: false,
  handleFavoriteClick: null
};

export default MapHeader;
